import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { itemsApi, type HouseholdItem } from '../api/items';
import { PageHeader } from '../components/ui/PageHeader';
import { Input, Textarea } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import { PlusIcon, CheckIcon } from '../components/ui/icons';
import { suggestCategory } from '../lib/categoryDictionary';

const CATEGORY_SUGGESTIONS = [
  'Alimentos',
  'Laticínios',
  'Limpeza',
  'Higiene',
  'Bebidas',
  'Hortifruti',
  'Padaria',
  'Carnes',
  'Congelados',
  'Outros',
];

interface FormData {
  name: string;
  category: string;
  quantityAtHome: string;
  defaultQuantityToBuy: string;
  notes: string;
}

export function ItemFormScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [form, setForm] = useState<FormData>({
    name: '',
    category: '',
    quantityAtHome: '',
    defaultQuantityToBuy: '1',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [categoryTouched, setCategoryTouched] = useState(false);

  const { data: item, isLoading: isLoadingItem } = useQuery({
    queryKey: ['items', id],
    queryFn: () => itemsApi.get(id!),
    enabled: isEditing,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name,
        category: item.category ?? '',
        quantityAtHome: item.quantityAtHome ?? '',
        defaultQuantityToBuy: item.defaultQuantityToBuy ?? '1',
        notes: item.notes ?? '',
      });
      setCategoryTouched(true);
    }
  }, [item]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<HouseholdItem>) => itemsApi.create(data),
    onSuccess: (newItem) => {
      queryClient.setQueryData<HouseholdItem[]>(['items'], (old) =>
        old ? [...old, newItem] : [newItem]
      );
      addToast('Item criado com sucesso!', 'success');
      navigate('/items');
    },
    onError: () => addToast('Erro ao criar item. Tente novamente.', 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<HouseholdItem>) => itemsApi.update(id!, data),
    onSuccess: (updated) => {
      queryClient.setQueryData<HouseholdItem[]>(['items'], (old) =>
        old ? old.map((i) => (i.id === updated.id ? updated : i)) : [updated]
      );
      queryClient.setQueryData(['items', id], updated);
      addToast('Item atualizado!', 'success');
      navigate('/items');
    },
    onError: () => addToast('Erro ao atualizar item.', 'error'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Partial<FormData> = {};
    if (!form.name.trim()) newErrors.name = 'Informe o nome do item';
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});

    const payload: Partial<HouseholdItem> = {
      name: form.name.trim(),
      category: form.category.trim() || null,
      quantityAtHome: form.quantityAtHome.trim() || null,
      defaultQuantityToBuy: form.defaultQuantityToBuy.trim() || '1',
      notes: form.notes.trim() || null,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const filteredSuggestions = CATEGORY_SUGGESTIONS.filter(
    (s) => !form.category || s.toLowerCase().includes(form.category.toLowerCase())
  );

  if (isEditing && isLoadingItem) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader
        title={isEditing ? 'Editar Item' : 'Novo Item'}
        showBack
      />

      <form onSubmit={handleSubmit} className="flex-1 px-4 py-5 space-y-4">
        <div className="flex flex-col gap-4">
          <Input
            label="Nome"
            placeholder="ex: Arroz, Detergente, Shampoo"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm((f) => {
                if (categoryTouched || f.category) return { ...f, name };
                const suggested = suggestCategory(name);
                return suggested ? { ...f, name, category: suggested } : { ...f, name };
              });
            }}
            error={errors.name}
            name="name"
            autoFocus={!isEditing}
            required
          />

          {/* Category with suggestions */}
          <div className="flex flex-col gap-1 relative">
            <Input
              label="Categoria"
              placeholder="ex: Alimentos, Limpeza, Higiene"
              value={form.category}
              onChange={(e) => {
                setForm((f) => ({ ...f, category: e.target.value }));
                setCategoryTouched(true);
                setShowCategorySuggestions(true);
              }}
              hint={
                !categoryTouched && form.category
                  ? `Sugerido automaticamente com base no nome`
                  : undefined
              }
              onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 150)}
              name="category"
            />
            {showCategorySuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden mt-1">
                {filteredSuggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="w-full text-left px-4 py-3 text-sm hover:bg-emerald-50 text-gray-700 transition-colors"
                    onMouseDown={() => {
                      setForm((f) => ({ ...f, category: s }));
                      setShowCategorySuggestions(false);
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Qty + Qty-to-buy side by side on wide screens, stacked on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Qtd em casa"
            placeholder="ex: 1, 2kg, 500ml"
            value={form.quantityAtHome}
            onChange={(e) => setForm((f) => ({ ...f, quantityAtHome: e.target.value }))}
            name="quantityAtHome"
          />
          <Input
            label="Qtd a comprar"
            placeholder="ex: 2, 1kg (0 p/ não comprar)"
            value={form.defaultQuantityToBuy}
            onChange={(e) => setForm((f) => ({ ...f, defaultQuantityToBuy: e.target.value }))}
            name="defaultQuantityToBuy"
          />
        </div>

        <Textarea
          label="Notas (opcional)"
          placeholder="Marca preferida, onde comprar, etc."
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          name="notes"
          rows={2}
        />

        {/* Footer bar: label + icon-only save button (esp. for onepage flow) */}
        <div className="pt-2 flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-gray-900">
            {isEditing ? 'Editar item' : 'Novo item'}
          </p>
          <button
            type="submit"
            disabled={isLoading}
            title={isEditing ? 'Salvar' : 'Adicionar'}
            aria-label={isEditing ? 'Salvar' : 'Adicionar item'}
            className="w-12 h-12 bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white rounded-xl flex items-center justify-center transition-colors disabled:bg-brand-300 disabled:opacity-50 touch-manipulation"
          >
            {isEditing ? <CheckIcon size={20} /> : <PlusIcon size={20} />}
          </button>
        </div>
      </form>
    </div>
  );
}
